import { createSlice } from "@reduxjs/toolkit";

const initialContainerState = {
  list: {
    listLoading: false,
    actionsLoading: false,
    totalCount: 0,
    entities: null,
    productForEdit: undefined,
    lastError: null,
    // The newest request for this list; an older response carrying a different
    // id is discarded rather than overwriting it (see fetchContainerList).
    requestId: 0,
  },
  conLoading: false,
  // The newest franchise-container request; an older response carrying a
  // different id is discarded rather than overwriting it (see
  // fetchContainerFranchise).
  containerFranchise: { list: [], totalCount: 0, requestId: 0 },
};
export const callTypes = {
  list: "list",
  listAll: "listAll",
  action: "action",
};

/**
 * Whether this response belongs to a superseded list request.
 *
 * @param {Object} state Slice state
 * @param {Object} payload Action payload, which may carry a `requestId`
 * @returns {boolean} True when a newer request has since been started
 */
const isStaleFranchiseResponse = (state, payload) =>
  payload.requestId !== undefined &&
  state.containerFranchise.requestId !== 0 &&
  payload.requestId !== state.containerFranchise.requestId;

/**
 * Whether this response belongs to a superseded container-list request.
 *
 * @param {Object} state Slice state
 * @param {Object} payload Action payload, which may carry a `requestId`
 * @returns {boolean} True when a newer request has since been started
 */
const isStaleListResponse = (state, payload) =>
  payload.requestId !== undefined &&
  state.list.requestId !== 0 &&
  payload.requestId !== state.list.requestId;

/**
 * Whether a call's payload belongs to a superseded request, for whichever of
 * the two lists it came from. Anything else is never stale.
 *
 * @param {Object} state Slice state
 * @param {Object} payload Action payload, which may carry a `requestId`
 * @returns {boolean} True when a newer request has since been started
 */
const isStaleResponse = (state, payload) => {
  if (payload.callType === callTypes.list) {
    return isStaleFranchiseResponse(state, payload);
  }

  if (payload.callType === callTypes.listAll) {
    return isStaleListResponse(state, payload);
  }

  return false;
};

export const containerSlice = createSlice({
  name: "Container",
  initialState: initialContainerState,
  reducers: {
    /**
     * Record a failed call and clear the loading flag it set.
     *
     * @param {Object} state Slice state
     * @param {Object} action Carries `error`, `callType` and, for list calls, `requestId`
     * @returns {void}
     */
    catchError: (state, action) => {
      // Before state.error: a superseded request's failure must not be shown
      // beside the rows the newest request went on to load successfully.
      if (isStaleResponse(state, action.payload)) {
        return;
      }

      state.error = `${action.type}: ${action.payload.error}`;
      if (action.payload.callType === callTypes.list) {
        state.conLoading = false;
      } else if (action.payload.callType === callTypes.listAll) {
        state.list.listLoading = false;
      } else {
        state.actionsLoading = false;
      }
    },
    /**
     * Mark a call as running, remembering which list request is the newest.
     *
     * @param {Object} state Slice state
     * @param {Object} action Carries `callType` and, for list calls, `requestId`
     * @returns {void}
     */
    startCall: (state, action) => {
      state.error = null;
      if (action.payload.callType === callTypes.list) {
        if (action.payload.requestId !== undefined) {
          state.containerFranchise.requestId = action.payload.requestId;
        }
        state.conLoading = true;
      } else if (action.payload.callType === callTypes.listAll) {
        if (action.payload.requestId !== undefined) {
          state.list.requestId = action.payload.requestId;
        }
        state.list.listLoading = true;
      } else {
        state.list.actionsLoading = true;
      }
    },

    /**
     * Store a container list response, unless a newer request has started.
     *
     * @param {Object} state Slice state
     * @param {Object} action Carries `requestId`, `entities` and `totalCount`
     * @returns {void}
     */
    ContainerFetchedList: (state, action) => {
      if (isStaleListResponse(state, action.payload)) {
        return;
      }

      const { totalCount, entities } = action.payload;
      state.list.listLoading = false;
      state.list.error = null;
      state.list.entities = entities;
      state.list.totalCount = totalCount;
    },

    /**
     * Store a franchise's containers, unless a newer request has started.
     *
     * @param {Object} state Slice state
     * @param {Object} action Carries `requestId`, `data` and `totalCount`
     * @returns {void}
     */
    ContainerFetchedFranchise: (state, action) => {
      if (isStaleFranchiseResponse(state, action.payload)) {
        return;
      }

      const { data, totalCount } = action.payload;
      state.containerFranchise.list = data;
      state.containerFranchise.totalCount = totalCount
      state.conLoading = false
    },
  },
});
